import MenuItemModel from '../models/nlp/menu/MenuItemModel';
import IResult from '../models/result/IResult';
import ApplicationModel from '../models/applications/api/ApplicationModel';
import { DeepAnalysisUpsellSummaryByApplication, DeepAnalysisUpsellValuesByTag } from '../api/controllers/engagementAnalyticsReporting';
import { runAnalyticsRequests } from './analyticsRequestQueue';

interface MenuPriceMatch {
    itemKey: number;
    price: number | null;
}

export type MenuPriceIndex = Map<string, MenuPriceMatch[]>;
export type MenuPricesByApplication = Record<string, MenuPriceIndex>;
export type MenuEstimateOverridesByEntity = Record<string, Record<string, number>>;
const MANUAL_OVERRIDES_KEY = 'UPSELL_ESTIMATE_MANUAL_OVERRIDES';

// The API represents both an unset estimate and an explicit zero as 0.
// Retain successful manual saves locally so a refresh can distinguish the two.
export const readMenuEstimateOverrides = (storage?: Pick<Storage, 'getItem'>): MenuEstimateOverridesByEntity => {
    try {
        const stored = JSON.parse((storage || window.localStorage).getItem(MANUAL_OVERRIDES_KEY) || '{}');
        const overrides: MenuEstimateOverridesByEntity = {};
        if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return overrides;
        Object.entries(stored).forEach(([entityId, values]) => {
            if (!values || typeof values !== 'object' || Array.isArray(values)) return;
            overrides[entityId] = Object.fromEntries(Object.entries(values)
                .filter(([, value]) => typeof value === 'number' && Number.isFinite(value) && value >= 0));
        });
        return overrides;
    } catch {
        return {};
    }
};

export const storeMenuEstimateOverrides = (overrides: MenuEstimateOverridesByEntity, storage?: Pick<Storage, 'setItem'>): void => {
    try {
        (storage || window.localStorage).setItem(MANUAL_OVERRIDES_KEY, JSON.stringify(overrides));
    } catch {
        // The server remains the source of truth if browser storage is unavailable.
    }
};

export const getSavedMenuOverrideKeys = (
    values: DeepAnalysisUpsellValuesByTag,
    overrides: Record<string, number> = {}
): Set<string> => new Set(Object.entries(values).filter(([tag, value]) =>
    Object.prototype.hasOwnProperty.call(overrides, tag.toLocaleLowerCase()) &&
    value?.dollarValue === overrides[tag.toLocaleLowerCase()]).map(([tag]) => tag.toLocaleLowerCase()));

// Tags are slugs; only remove the event marker, never guess a similar item name.
export const normalizeUpsellMenuName = (value: string): string => (value || '')
    .toLowerCase().replace(/&/g, ' and ').replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ').trim()
    .replace(/^upsold\s+|\s+upsold$/g, '').trim();

const parsePrice = (value?: string): number | null => {
    const text = typeof value === 'string' ? value.trim().replace(/^\$\s*/, '') : '';
    if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(text)) return null;
    const price = Number(text.replace(/,/g, ''));
    return Number.isFinite(price * 100) ? Math.round(price * 100) / 100 : null;
};

export const buildMenuPriceIndex = (items: MenuItemModel[]): MenuPriceIndex => {
    const index: MenuPriceIndex = new Map();
    const add = (names: string[], price: number | null, itemKey: number) => {
        new Set(names.map(normalizeUpsellMenuName).filter(Boolean)).forEach(name => {
            index.set(name, [...(index.get(name) || []), { itemKey, price }]);
        });
    };
    items.forEach((item, itemKey) => {
        const names = [item.originalName, item.colloquialName, item.fullName, ...(item.additionalSynonyms || [])].filter(Boolean);
        const sizes = (item.sizeGroups || []).flatMap(group => group.sizes || []);
        // A base price must not hide an unresolved choice of size.
        const sizePrices = sizes.map(size => parsePrice(size.attributes?.data?.price));
        const hasSizePricing = sizes.some(size => size.attributes?.data?.price != null && size.attributes.data.price !== '');
        const price = hasSizePricing
            ? sizePrices.every(value => value !== null && value === sizePrices[0]) ? sizePrices[0] : null
            : parsePrice(item.attributes?.data?.price);
        add(names, price, itemKey);
        sizes.forEach(size => {
            const sizeNames = [size.name, size.colloquialName, ...(size.additionalSynonyms || [])].filter(Boolean);
            add(names.flatMap(name => sizeNames.flatMap(sizeName => [`${sizeName} ${name}`, `${name} ${sizeName}`])),
                hasSizePricing ? parsePrice(size.attributes?.data?.price) : price, itemKey);
        });
    });
    return index;
};

export const resolveMenuPrice = (index: MenuPriceIndex | undefined, tag: string): number | null => {
    const name = normalizeUpsellMenuName(tag);
    if (!index || !name) return null;
    const exact = index.get(name);
    const matches = exact?.length ? exact : Array.from(index.entries())
        .filter(([menuName]) => ` ${menuName} `.includes(` ${name} `))
        .flatMap(([, candidates]) => candidates);
    // Aliases for one item are fine; different items or unresolved sizes are not.
    const itemKeys = new Set(matches.map(match => match.itemKey));
    const prices = new Set(matches.map(match => match.price));
    return itemKeys.size === 1 && prices.size === 1 ? matches[0].price : null;
};

export const loadLocationMenuPrices = async (
    applicationIds: string[],
    loadApplication: (applicationId: string) => Promise<IResult<Pick<ApplicationModel, 'manageMenuApplicationId'>>>,
    loadMenu: (applicationId: string) => Promise<IResult<MenuItemModel[]>>,
    signal?: AbortSignal
): Promise<MenuPricesByApplication> => {
    const menus: MenuPricesByApplication = {};
    const menuRequests = new Map<string, Promise<IResult<MenuItemModel[]>>>();
    await runAnalyticsRequests(Array.from(new Set(applicationIds)).map(applicationId => async () => {
        try {
            const application = await loadApplication(applicationId);
            if (application.resultType !== 'Ok' || !application.data || signal?.aborted) {
                menus[applicationId] = buildMenuPriceIndex([]);
                return;
            }
            // Follow the same location -> menu link used by the Menu screen.
            const menuApplicationId = application.data.manageMenuApplicationId || applicationId;
            if (!menuRequests.has(menuApplicationId)) menuRequests.set(menuApplicationId, loadMenu(menuApplicationId));
            const result = await menuRequests.get(menuApplicationId);
            menus[applicationId] = buildMenuPriceIndex(result.resultType === 'Ok' ? result.data || [] : []);
        } catch {
            menus[applicationId] = buildMenuPriceIndex([]);
        }
    }), 4, signal);
    return menus;
};

// One editor is saved across locations. Only prefill a common,
// uniquely matched price; otherwise leave the existing estimate untouched.
export const getCommonMenuPrice = (
    menus: MenuPricesByApplication,
    applicationIds: string[],
    tag: string
): number | null => {
    if (!applicationIds.length) return null;
    const prices = applicationIds.map(applicationId => resolveMenuPrice(menus[applicationId], tag));
    return prices[0] !== null && prices.every(price => price === prices[0]) ? prices[0] : null;
};

export const getMenuPricesByUpsell = (
    menus: MenuPricesByApplication,
    summaries: DeepAnalysisUpsellSummaryByApplication
): Record<string, number> => {
    const locationsByTag = new Map<string, Set<string>>();
    Object.entries(summaries).forEach(([applicationId, summary]) => {
        (summary.tagsTotals || []).forEach(({ tag, count }) => {
            if (!tag || !(count > 0)) return;
            const key = tag.toLocaleLowerCase();
            const locations = locationsByTag.get(key) || new Set<string>();
            locations.add(applicationId);
            locationsByTag.set(key, locations);
        });
    });
    const prices: Record<string, number> = {};
    locationsByTag.forEach((applicationIds, tag) => {
        const price = getCommonMenuPrice(menus, Array.from(applicationIds), tag);
        if (price !== null) prices[tag] = price;
    });
    return prices;
};

export const buildMenuEstimateUpdates = (
    values: DeepAnalysisUpsellValuesByTag,
    prices: Record<string, number>,
    protectedKeys: Set<string> = new Set()
): DeepAnalysisUpsellValuesByTag | null => {
    const nextValues = { ...values };
    let changed = false;
    Object.entries(prices).forEach(([tag, price]) => {
        if (!Number.isFinite(price) || price < 0) return;
        const key = Object.keys(values).find(key => key.toLocaleLowerCase() === tag.toLocaleLowerCase()) || tag;
        const value = values[key];
        if (value?.hidden || value?.isHidden || value?.dollarValue === price ||
            protectedKeys.has(key.toLocaleLowerCase())) return;
        nextValues[key] = { ...value, displayName: value?.displayName || '', dollarValue: price };
        changed = true;
    });
    return changed ? nextValues : null;
};

export const getEstimateSaveError = (result?: IResult<unknown>): string => {
    if (result?.httpStatus === 401 || result?.resultType === 'Unauthorized')
        return 'Your sign-in has expired. Sign in again, then try saving.';
    if (result?.httpStatus === 403 || result?.resultType === 'PermissionDenied')
        return 'Your account cannot change estimated values. Ask your organization administrator for access.';
    if (result?.httpStatus === 429)
        return 'Too many changes were sent at once. Wait a moment, then try saving again.';
    if (result?.httpStatus >= 500 || result?.resultType === 'InternalError')
        return 'The save service is temporarily unavailable. Try again in a few minutes.';
    if (result?.httpStatus === 404 || result?.resultType === 'NotFound')
        return 'The save service could not find this organization. Refresh the page and try again. If it still fails, contact support.';
    if (result?.httpStatus === 400 || result?.resultType === 'Invalid')
        return 'The save service could not accept this change. Refresh the page and try saving again. If it still fails, contact support.';
    if (result?.httpStatus || result?.resultType === 'Ok')
        return 'The save service did not confirm the change. Refresh the page and try saving again.';
    return 'We could not reach the save service. Check your internet connection, then try saving again.';
};

export const getConfirmedEstimateValues = (
    result: IResult<DeepAnalysisUpsellValuesByTag>,
    requested: DeepAnalysisUpsellValuesByTag,
    changedTags: string[]
): DeepAnalysisUpsellValuesByTag | null => {
    if (result.resultType !== 'Ok') return null;
    // A successful empty response (including 204) is an acknowledgement,
    // not a save failure. Keep the acknowledged request as the saved state.
    if (result.data == null) return requested;
    if (typeof result.data !== 'object' || Array.isArray(result.data)) return null;
    const returnedKeys = Object.keys(result.data);
    if (!changedTags.every(tag => {
        const returnedKey = returnedKeys.find(key => key.toLocaleLowerCase() === tag.toLocaleLowerCase());
        return returnedKey !== undefined && result.data[returnedKey]?.dollarValue === requested[tag]?.dollarValue;
    })) return null;
    const confirmed = { ...requested };
    returnedKeys.forEach(tag => {
        const key = Object.keys(requested).find(key => key.toLocaleLowerCase() === tag.toLocaleLowerCase()) || tag;
        confirmed[key] = { ...requested[key], ...result.data[tag] };
    });
    return confirmed;
};

export const describeEstimateSaveFailures = (messages: Record<string, string>, savedCount: number): string => {
    const prefix = savedCount > 0 ? 'Some changes were saved, but others were not. ' : 'This amount was not saved. ';
    return prefix + Array.from(new Set(Object.values(messages))).join(' ');
};

export const applySavedEstimateRevenue = (
    summaries: DeepAnalysisUpsellSummaryByApplication,
    values: DeepAnalysisUpsellValuesByTag,
    changedTags: string[]
): DeepAnalysisUpsellSummaryByApplication => {
    const changed = new Set(changedTags.map(tag => tag.toLocaleLowerCase()));
    const valuesByTag = new Map(Object.entries(values).map(([tag, value]) => [tag.toLocaleLowerCase(), value]));
    return Object.fromEntries(Object.entries(summaries).map(([applicationId, summary]) => {
        const totals = new Map((summary.upsellValues || []).map(value => [value['upsold-item'].toLocaleLowerCase(), value]));
        const counts = new Map<string, number>();
        (summary.tagsTotals || []).forEach(({ tag, count }) => {
            const key = tag.toLocaleLowerCase();
            counts.set(key, (counts.get(key) || 0) + count);
        });
        counts.forEach((count, tag) => {
            const price = valuesByTag.get(tag)?.dollarValue;
            if (!changed.has(tag) || !Number.isFinite(price) || price < 0) return;
            totals.set(tag, { 'upsold-item': tag, 'total-dollar-value': Math.round(count * price * 100) / 100 });
        });
        return [applicationId, { ...summary, upsellValues: Array.from(totals.values()) }];
    }));
};

// The API replaces an organization's whole value map. Batch its matched items
// into one write and serialize organizations rather than issuing per-row writes.
export const saveMenuEstimateUpdates = async (
    configs: Record<string, DeepAnalysisUpsellValuesByTag>,
    pricesByEntity: Record<string, Record<string, number>>,
    save: (entityId: string, values: DeepAnalysisUpsellValuesByTag) => Promise<IResult<DeepAnalysisUpsellValuesByTag>>,
    overrides: MenuEstimateOverridesByEntity = {}
) => {
    const saved: Record<string, DeepAnalysisUpsellValuesByTag> = {};
    const failed: string[] = [];
    const failureMessages: Record<string, string> = {};
    for (const entityId of Object.keys(pricesByEntity)) {
        if (configs[entityId] === undefined) {
            failed.push(entityId);
            failureMessages[entityId] = 'We could not load the saved estimates. Refresh the page, then try saving again.';
            continue;
        }
        const nextValues = buildMenuEstimateUpdates(configs[entityId], pricesByEntity[entityId],
            getSavedMenuOverrideKeys(configs[entityId], overrides[entityId]));
        if (!nextValues) continue;
        try {
            const result = await save(entityId, nextValues);
            const changedTags = Object.keys(nextValues).filter(tag => nextValues[tag].dollarValue !== configs[entityId][tag]?.dollarValue);
            const confirmed = getConfirmedEstimateValues(result, nextValues, changedTags);
            if (confirmed) {
                saved[entityId] = confirmed;
            } else {
                failed.push(entityId);
                failureMessages[entityId] = getEstimateSaveError(result);
            }
        } catch {
            failed.push(entityId);
            failureMessages[entityId] = getEstimateSaveError();
        }
    }
    return { saved, failed, failureMessages };
};
